/**
 * ============================================================================
 * VOCALIRA CORE CONVERSATION ENGINE (VCCE) - Platform Configuration Manager
 * ============================================================================
 * 
 * SPRINT 2: Mobile-First Voice Engine
 * 
 * Este módulo detecta a plataforma e fornece configurações otimizadas.
 * NÃO altera o pipeline VCCE, apenas fornece parâmetros adaptados.
 * 
 * Plataformas suportadas:
 * - Desktop (Chrome, Firefox, Edge, Safari)
 * - Android (Chrome, Samsung Internet, Edge, Firefox)
 * - iOS (Safari, Chrome, PWA)
 * - Tablet (iPad, Android Tablet)
 * 
 * @author Vocalira Team
 * @version 2.1.0 - Mobile-First Architecture
 */

// ============================================================================
// DETECÇÃO DE PLATAFORMA
// ============================================================================

/**
 * Detecta informações detalhadas sobre a plataforma.
 * @returns {Object} Informações da plataforma
 */
function detectPlatform() {
  const ua = navigator.userAgent || '';
  const vendor = navigator.vendor || '';
  const platform = navigator.platform || '';
  
  // Detecção de dispositivo
  const isIPhone = /iPhone/i.test(ua);
  const isIPad = /iPad/i.test(ua) || (platform === 'MacIntel' && navigator.maxTouchPoints > 1);
  const isAndroid = /Android/i.test(ua);
  const isAndroidTablet = isAndroid && !/Mobile/i.test(ua);
  const isMobile = /Mobile|Android|iPhone|iPod/i.test(ua) && !isIPad && !isAndroidTablet;
  const isTablet = isIPad || isAndroidTablet;
  
  // Detecção de OS
  const isIOS = isIPhone || isIPad;
  const isMacOS = /Mac/i.test(platform) && !isIOS;
  const isWindows = /Win/i.test(platform);
  const isLinux = /Linux/i.test(platform) && !isAndroid;
  
  // Detecção de navegador
  const isChrome = /Chrome/i.test(ua) && /Google Inc/i.test(vendor);
  const isSafari = /Safari/i.test(ua) && /Apple/i.test(vendor);
  const isFirefox = /Firefox/i.test(ua);
  const isEdge = /Edg/i.test(ua);
  const isSamsungInternet = /SamsungBrowser/i.test(ua);
  const isOpera = /OPR/i.test(ua);
  
  // Detecção de contexto especial
  const isPWA = window.matchMedia('(display-mode: standalone)').matches ||
                window.navigator.standalone === true;
  const isWebView = /(wv|WebView)/i.test(ua) || 
                    (isIOS && !isSafari && !isChrome);
  const isSecureContext = window.isSecureContext;
  
  // Detecção de capacidades
  const hasGetUserMedia = !!(navigator.mediaDevices && navigator.mediaDevices.getUserMedia);
  const hasSpeechRecognition = !!(window.SpeechRecognition || window.webkitSpeechRecognition);
  const hasAudioContext = !!(window.AudioContext || window.webkitAudioContext);
  const hasTouchScreen = 'ontouchstart' in window || navigator.maxTouchPoints > 0;
  
  // Versão do iOS (se aplicável)
  let iOSVersion = null;
  if (isIOS) {
    const match = ua.match(/OS (\d+)_(\d+)/);
    if (match) {
      iOSVersion = parseFloat(`${match[1]}.${match[2]}`);
    }
  }
  
  // Versão do Android (se aplicável)
  let androidVersion = null;
  if (isAndroid) {
    const match = ua.match(/Android (\d+\.?\d*)/);
    if (match) {
      androidVersion = parseFloat(match[1]);
    }
  }
  
  return {
    // Tipo de dispositivo
    isMobile,
    isTablet,
    isDesktop: !isMobile && !isTablet,
    
    // Sistema operacional
    isIOS,
    isAndroid,
    isMacOS,
    isWindows,
    isLinux,
    
    // Dispositivo específico
    isIPhone,
    isIPad,
    isAndroidTablet,
    
    // Navegador
    isChrome,
    isSafari,
    isFirefox,
    isEdge,
    isSamsungInternet,
    isOpera,
    
    // Contexto especial
    isPWA,
    isWebView,
    isSecureContext,
    
    // Capacidades
    hasGetUserMedia,
    hasSpeechRecognition,
    hasAudioContext,
    hasTouchScreen,
    
    // Versões
    iOSVersion,
    androidVersion,
    
    // String de identificação para logs
    platformId: _buildPlatformId({
      isMobile, isTablet, isIOS, isAndroid, isChrome, isSafari, 
      isFirefox, isEdge, isSamsungInternet, isPWA, isWebView
    }),
    
    // User Agent completo (para diagnóstico)
    userAgent: ua,
  };
}

/**
 * Constrói uma string de identificação da plataforma.
 */
function _buildPlatformId(p) {
  let device = p.isMobile ? 'Mobile' : p.isTablet ? 'Tablet' : 'Desktop';
  let os = p.isIOS ? 'iOS' : p.isAndroid ? 'Android' : 'Other';
  let browser = p.isChrome ? 'Chrome' : p.isSafari ? 'Safari' : 
                p.isFirefox ? 'Firefox' : p.isEdge ? 'Edge' : 
                p.isSamsungInternet ? 'Samsung' : 'Other';
  let context = p.isPWA ? 'PWA' : p.isWebView ? 'WebView' : 'Browser';
  
  return `${device}/${os}/${browser}/${context}`;
}

// ============================================================================
// CONFIGURAÇÕES POR PLATAFORMA
// ============================================================================

/**
 * Configurações base (Desktop)
 */
const BASE_CONFIG = {
  // Audio
  preferredSampleRate: 16000,
  fftSize: 2048,
  smoothingTimeConstant: 0.3,
  
  // VAD Thresholds
  vadSpeechThreshold: 0.015,
  vadSilenceThreshold: 0.012,
  vadSpeechStartDelay: 80,
  
  // Noise Floor
  noiseFloorEstimationTime: 500,
  noiseFloorMultiplier: 1.5,
  
  // Voice Frequency
  voiceFreqMin: 85,
  voiceFreqMax: 3000,
  voiceFreqRatio: 0.45,
  
  // STT
  sttRestartDelay: 100,
  sttMaxRestartAttempts: 3,
  sttRestartBackoffBase: 200,
  sttContinuous: true,
  sttInterimResults: true,
  
  // Timeouts
  maxIdleTime: 5000,
  maxSpeechTime: 60000,
  sttFinalCooldown: 1500,
  speechEndCooldown: 800,
  
  // getUserMedia
  audioConstraints: {
    echoCancellation: true,
    noiseSuppression: true,
    autoGainControl: true,
  },
  
  // Recovery
  maxRecoveryAttempts: 3,
  recoveryDelay: 500,
  
  // AudioContext
  requiresUserGestureUnlock: false,
  audioContextResumeTimeout: 1000,
};

/**
 * Overrides para Android
 */
const ANDROID_CONFIG = {
  // VAD mais sensível (microfones mobile são diferentes)
  vadSpeechThreshold: 0.012,
  vadSilenceThreshold: 0.008,
  
  // STT para mais rápido em Android
  sttRestartDelay: 300,
  sttMaxRestartAttempts: 5,
  
  // Timeouts mais generosos
  maxIdleTime: 7000,
  speechEndCooldown: 1000,
  
  // Constraints flexíveis
  audioConstraints: {
    echoCancellation: { ideal: true },
    noiseSuppression: { ideal: true },
    autoGainControl: { ideal: true },
  },
  
  // Recovery mais agressivo
  maxRecoveryAttempts: 5,
  recoveryDelay: 300,
};

/**
 * Overrides para iOS Safari
 */
const IOS_CONFIG = {
  // VAD ajustado para iOS
  vadSpeechThreshold: 0.010,
  vadSilenceThreshold: 0.006,
  
  // STT em iOS é menos confiável
  sttRestartDelay: 400,
  sttMaxRestartAttempts: 5,
  sttRestartBackoffBase: 300,
  
  // iOS precisa de unlock explícito
  requiresUserGestureUnlock: true,
  audioContextResumeTimeout: 2000,
  
  // Timeouts mais generosos para iOS
  maxIdleTime: 8000,
  sttFinalCooldown: 2000,
  speechEndCooldown: 1200,
  
  // Constraints flexíveis
  audioConstraints: {
    echoCancellation: { ideal: true },
    noiseSuppression: { ideal: true },
    autoGainControl: { ideal: true },
  },
  
  // Recovery
  maxRecoveryAttempts: 5,
  recoveryDelay: 400,
};

/**
 * Overrides para Samsung Internet
 */
const SAMSUNG_CONFIG = {
  ...ANDROID_CONFIG,
  
  // Samsung Internet tem comportamento específico
  vadSpeechThreshold: 0.014,
  sttRestartDelay: 350,
};

/**
 * Overrides para PWA
 */
const PWA_CONFIG = {
  // PWA pode ter comportamentos diferentes após background
  requiresUserGestureUnlock: true,
  maxRecoveryAttempts: 5,
};

/**
 * Overrides para WebView
 */
const WEBVIEW_CONFIG = {
  // WebViews são menos confiáveis
  sttMaxRestartAttempts: 7,
  maxRecoveryAttempts: 7,
  recoveryDelay: 600,
  
  // Constraints muito flexíveis
  audioConstraints: {
    echoCancellation: { ideal: true },
    noiseSuppression: { ideal: false }, // Pode causar problemas em WebView
    autoGainControl: { ideal: true },
  },
};

// ============================================================================
// PLATFORM CONFIGURATION MANAGER
// ============================================================================

/**
 * Gerenciador de configuração por plataforma.
 * Singleton que fornece configurações otimizadas baseadas na plataforma detectada.
 */
class PlatformConfigurationManager {
  constructor() {
    // Detectar plataforma uma vez
    this.platform = detectPlatform();
    
    // Construir configuração mesclada
    this.config = this._buildConfig();
    
    // Log de diagnóstico
    this._logPlatformInfo();
  }
  
  /**
   * Constrói a configuração final mesclando base + overrides.
   */
  _buildConfig() {
    let config = { ...BASE_CONFIG };
    
    // Aplicar overrides por OS
    if (this.platform.isIOS) {
      config = { ...config, ...IOS_CONFIG };
    } else if (this.platform.isAndroid) {
      config = { ...config, ...ANDROID_CONFIG };
    }
    
    // Aplicar overrides por navegador específico
    if (this.platform.isSamsungInternet) {
      config = { ...config, ...SAMSUNG_CONFIG };
    }
    
    // Aplicar overrides por contexto
    if (this.platform.isPWA) {
      config = { ...config, ...PWA_CONFIG };
    }
    
    if (this.platform.isWebView) {
      config = { ...config, ...WEBVIEW_CONFIG };
    }
    
    return config;
  }
  
  /**
   * Log de informações da plataforma para diagnóstico.
   */
  _logPlatformInfo() {
    console.log('[PlatformConfig] Platform detected:', {
      platformId: this.platform.platformId,
      isMobile: this.platform.isMobile,
      isTablet: this.platform.isTablet,
      isIOS: this.platform.isIOS,
      isAndroid: this.platform.isAndroid,
      isPWA: this.platform.isPWA,
      isWebView: this.platform.isWebView,
      iOSVersion: this.platform.iOSVersion,
      androidVersion: this.platform.androidVersion,
      hasGetUserMedia: this.platform.hasGetUserMedia,
      hasSpeechRecognition: this.platform.hasSpeechRecognition,
      hasAudioContext: this.platform.hasAudioContext,
    });
    
    console.log('[PlatformConfig] Applied configuration:', {
      vadSpeechThreshold: this.config.vadSpeechThreshold,
      vadSilenceThreshold: this.config.vadSilenceThreshold,
      sttRestartDelay: this.config.sttRestartDelay,
      requiresUserGestureUnlock: this.config.requiresUserGestureUnlock,
      maxRecoveryAttempts: this.config.maxRecoveryAttempts,
    });
  }
  
  /**
   * Retorna a configuração completa.
   */
  getConfig() {
    return { ...this.config };
  }
  
  /**
   * Retorna informações da plataforma.
   */
  getPlatform() {
    return { ...this.platform };
  }
  
  /**
   * Verifica se é mobile (phone ou tablet).
   */
  isMobileDevice() {
    return this.platform.isMobile || this.platform.isTablet;
  }
  
  /**
   * Verifica se requer unlock de AudioContext.
   */
  requiresAudioUnlock() {
    return this.config.requiresUserGestureUnlock || 
           this.platform.isIOS || 
           this.platform.isPWA;
  }
  
  /**
   * Retorna constraints para getUserMedia.
   */
  getAudioConstraints() {
    return { audio: { ...this.config.audioConstraints } };
  }
  
  /**
   * Retorna fallback constraints (mais permissivo).
   */
  getFallbackAudioConstraints() {
    return { audio: true };
  }
  
  /**
   * Ajusta configuração de frequência baseado no sampleRate real.
   * @param {number} actualSampleRate - SampleRate real do AudioContext
   */
  adjustForSampleRate(actualSampleRate) {
    const ratio = actualSampleRate / this.config.preferredSampleRate;
    
    console.log('[PlatformConfig] Adjusting for actual sampleRate:', {
      preferred: this.config.preferredSampleRate,
      actual: actualSampleRate,
      ratio: ratio.toFixed(2),
    });
    
    // Ajustar FFT size proporcionalmente (manter resolução de frequência similar)
    if (ratio > 1.5) {
      // Se sampleRate é muito maior, aumentar FFT para manter resolução
      this.config.adjustedFftSize = Math.min(this.config.fftSize * 2, 4096);
    } else {
      this.config.adjustedFftSize = this.config.fftSize;
    }
    
    // Armazenar sampleRate real
    this.config.actualSampleRate = actualSampleRate;
    
    return {
      fftSize: this.config.adjustedFftSize,
      actualSampleRate,
    };
  }
  
  /**
   * Gera relatório de diagnóstico para logging.
   */
  getDiagnosticReport() {
    return {
      timestamp: new Date().toISOString(),
      platform: this.platform,
      config: this.config,
      capabilities: {
        getUserMedia: this.platform.hasGetUserMedia,
        speechRecognition: this.platform.hasSpeechRecognition,
        audioContext: this.platform.hasAudioContext,
        secureContext: this.platform.isSecureContext,
      },
    };
  }
}

// Singleton instance
let _instance = null;

/**
 * Obtém a instância singleton do PlatformConfigurationManager.
 */
function getPlatformConfig() {
  if (!_instance) {
    _instance = new PlatformConfigurationManager();
  }
  return _instance;
}

export default PlatformConfigurationManager;
export { 
  getPlatformConfig, 
  detectPlatform,
  BASE_CONFIG,
  ANDROID_CONFIG,
  IOS_CONFIG,
};
